import { Component, inject, OnInit, AfterViewInit, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TaskService } from '../../../shared/services/task.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { Task, TaskAuditEntry, ChecklistItem, ChecklistTemplate } from '../../../shared/models/task.model';
import { ProjectLink, ProjectStage } from '../../../shared/models/project.model';
import { ExternalLinkService } from '../../../shared/services/external-link.service';
import { ProjectService } from '../../../shared/services/project.service';
import { ChecklistService } from '../../../shared/services/checklist.service';
import { ClipboardService } from '../../../shared/services/clipboard.service';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../../shared/services/user.service';
import { User as UserModel } from '../../../shared/models/user.model';
import { User } from '../../../shared/services/user';
import { CommonModule } from '@angular/common';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { CommentsComponent } from '../../../shared/utils/comments/comments';
import { ConfirmModalComponent } from '../../../shared/utils/modal-validation/modal-validation';
import { RichTextEditorComponent } from '../../../shared/utils/rich-text-editor/rich-text-editor';
import { generateClient } from 'aws-amplify/api';
import { type Schema } from '../../../../../amplify/data/resource';

@Component({
  selector: 'app-add-task',
  imports: [FormsModule, CommonModule, DateInputsModule, CommentsComponent, ConfirmModalComponent, RichTextEditorComponent],
  templateUrl: './add-task.html',
  host: { class: 'block w-full' },
  // Borrowed for the ::ng-deep kendo datepicker popup rules (.kendo-datepicker-large
  // z-index, .k-calendar-container) that the date pickers below depend on.
  styleUrls: ['../../projects/display-projects/gantt-stage-project/details-task/details-task.scss']
})
export class AddTaskComponent implements OnInit, AfterViewInit, OnChanges {
  private taskService = inject(TaskService);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private externalLinkService = inject(ExternalLinkService);
  private projectService = inject(ProjectService);
  checklistService = inject(ChecklistService); // public: the template clears errors
  private clipboardService = inject(ClipboardService);
  user = inject(User); // For admin check
  private client = generateClient<Schema>();

  @Input() taskToEdit: Task | null = null;
  @Input() projectStageId!: string;
  @Input() stageStartDate?: string;
  @Input() stageEndDate?: string;
  @Input() projectTenantId?: string; // Add tenant ID for filtering users
  @Input() employees: UserModel[] = []; // In case they are passed directly
  @Input() projectManagerUserIds: string[] = [];
  /**
   * 'modal' when a caller wraps this card in an overlay (the default, and how the
   * detail views use it); 'page' when it is the main content of a route, where the
   * viewport cap and the drop shadow of a floating card are wrong.
   */
  @Input() presentation: 'modal' | 'page' = 'modal';

  /**
   * Shell sizing. A modal floats, is capped to the viewport and to a readable
   * width; a page fills the host its route gives it, so it must shed both caps —
   * otherwise the details view reads as a cramped modal dropped onto a page.
   */
  get shellClasses(): string {
    if (this.presentation === 'page') {
      return 'h-full max-w-none border border-gray-200';
    }
    const width = this.mode === 'view' && this.taskToEdit ? 'max-w-7xl' : 'max-w-4xl';
    return `${width} shadow-2xl max-h-[92vh]`;
  }

  @Output() close = new EventEmitter<void>();
  @Output() taskDeleted = new EventEmitter<void>();

  isSubmitting = false;
  loadingUsers = false;

  /** 'view' = read-only details; 'edit' = editable form. New tasks always start in edit. */
  mode: 'view' | 'edit' = 'edit';

  switchToEdit() {
    if (this.isTaskDone) return;
    this.mode = 'edit';
  }

  /** A completed task is read-only until it is reopened. */
  get isTaskDone(): boolean {
    return this.taskToEdit?.status === 'DONE';
  }

  isReopening = false;

  /** Send the task back to In Progress so it becomes editable again. */
  async reopenTask() {
    if (!this.taskToEdit?.id || this.isReopening) return;
    this.isReopening = true;
    try {
      const saved = await this.taskService.updateTask(this.taskToEdit.id, { status: 'INPROGRESS' });
      if (!saved) return;
      // The service replaces the object in its subject, so patch our own copy.
      this.taskToEdit = { ...this.taskToEdit, status: 'INPROGRESS', validatedBy: null, validatedAt: null };
      this.taskData.status = 'INPROGRESS';
      await this.loadAuditTrail();
    } catch (err) {
      console.error('[AddTask] Failed to reopen task:', err);
    } finally {
      this.isReopening = false;
    }
  }

  showConfirm = false;
  confirmMessage = '';
  currentProjectId: string = '';

  // For date constraints as Date objects for Kendo DatePicker
  minDate: Date = new Date();
  maxDate: Date = new Date(2100, 11, 31);
  startDateValue: Date | null = null;
  dueDateValue: Date | null = null;

  taskData = {
    nameTask: '',
    content: '',
    status: 'NOTBEGIN' as 'NOTBEGIN' | 'INPROGRESS' | 'DONE',
    priority: 'LOW' as 'LOW' | 'MEDIUM' | 'HIGH',
    startDate: '',
    limitDateToDo: '',
    estimatedTime: '' as string | number,
    employeeId: [] as string[],
    projectStageId: '',
    links: [] as ProjectLink[]
  };

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['taskToEdit'] && this.taskToEdit) {
      this.mode = 'view';
      this.populateEditData();
      await this.loadProjectId();
      await this.loadAuditTrail();
      await this.loadChecklist();
    }
  }

  ngOnInit() {
    this.loadStageNames();

    // Subscribed once, here rather than in loadChecklist(): a task being created
    // never calls that, and it would otherwise add a subscription per load.
    this.checklistService.error$.subscribe(message => this.checklistError = message);

    if (this.projectStageId && !this.taskToEdit) {
      this.taskData.projectStageId = this.projectStageId;
    }

    if (!this.taskToEdit) {
      this.mode = 'edit';
      this.initializeDatesForCreate();
    } else {
      this.mode = 'view';
      this.populateEditData();
      this.loadProjectId();
      this.loadAuditTrail();
      this.loadChecklist();
    }
  }

  private populateEditData() {
    if (!this.taskToEdit) return;

    this.taskData = {
      nameTask: this.taskToEdit.nameTask || '',
      content: this.taskToEdit.content || '',
      status: this.taskToEdit.status || 'NOTBEGIN',
      priority: this.taskToEdit.priority || 'LOW',
      startDate: '',
      limitDateToDo: '',
      estimatedTime: this.taskToEdit.estimatedHours ?? '',
      employeeId: (this.taskToEdit.employeeId || []).filter((id): id is string => id !== null),
      projectStageId: this.taskToEdit.projectStageId || this.projectStageId,
      links: [...(this.taskToEdit.links || [])]
    };

    if (this.taskToEdit.startDate) {
      const start = new Date(this.taskToEdit.startDate);
      this.startDateValue = start;
      const year = start.getFullYear();
      const month = String(start.getMonth() + 1).padStart(2, '0');
      const day = String(start.getDate()).padStart(2, '0');
      this.taskData.startDate = `${year}-${month}-${day}`;
    }

    if (this.taskToEdit.limitDateToDo) {
      const due = new Date(this.taskToEdit.limitDateToDo);
      this.dueDateValue = due;
      const year = due.getFullYear();
      const month = String(due.getMonth() + 1).padStart(2, '0');
      const day = String(due.getDate()).padStart(2, '0');
      this.taskData.limitDateToDo = `${year}-${month}-${day}`;
    }
  }

  private initializeDatesForCreate() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // New tasks always start today; the phase start only widens the pickable range.
    this.minDate = today;
    if (this.stageStartDate) {
      const parts = this.stageStartDate.split('-');
      const stageStart = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      stageStart.setHours(0, 0, 0, 0);
      if (stageStart < today) this.minDate = stageStart;
    }

    this.startDateValue = new Date(today);
    this.taskData.startDate = this.toIsoDay(this.startDateValue);

    if (this.stageEndDate) {
      const endParts = this.stageEndDate.split('-');
      this.maxDate = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
    }

    // Due date is left empty on purpose — it's required, the user must pick one.
    this.dueDateValue = null;
    this.taskData.limitDateToDo = '';
  }

  /** Local-time `YYYY-MM-DD` (avoids the UTC shift of toISOString). */
  private toIsoDay(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** Due date is mandatory when creating a task. */
  get isDueDateMissing(): boolean {
    return !this.taskToEdit && !this.taskData.limitDateToDo;
  }

  async ngAfterViewInit() {
    // If employees were passed directly (e.g., from kanban/gantt view), use them
    if (this.employees.length > 0) return;

    if (this.projectTenantId) {
      this.loadingUsers = true;
      try {
        this.employees = await this.userService.getUsersByTenantId(this.projectTenantId);
      } catch (error) {
        console.error('Failed to load tenant users:', error);
      } finally {
        this.loadingUsers = false;
      }
    } else {
      this.loadingUsers = true;
      const subscription = this.userService.users$.subscribe(employees => {
        this.employees = employees;
        this.loadingUsers = false;
        subscription.unsubscribe();
      });
    }
  }

  /** Project / phase / client shown in the left rail of the details view. */
  taskContext = { projectName: '', stageName: '', clientName: '' };

  private async loadProjectId() {
    try {
      const stageId = this.taskToEdit?.projectStageId || this.projectStageId;
      if (!stageId) return;

      const { data: stage } = await (this.client.models.ProjectStage.get as any)(
        { id: stageId },
        { selectionSet: ['id', 'stageName', 'projectId', 'project.projectName', 'project.client.name'] }
      );
      if (!stage) return;
      this.currentProjectId = stage.projectId;
      this.taskContext = {
        projectName: stage.project?.projectName ?? '',
        stageName: stage.stageName ?? '',
        clientName: stage.project?.client?.name ?? '',
      };
    } catch (err) {
      console.error('Error while getting projectId', err);
    }
  }

  // ── Copy to clipboard ───────────────────────────────────────────────────────

  /** Set briefly after a successful copy, to confirm it in the button. */
  copied = false;
  private copiedTimer?: ReturnType<typeof setTimeout>;

  /**
   * Plain-text rendering of the task: title, description, then the checklist as
   * markdown tick-boxes, which paste readably into Slack, Notion or a ticket.
   */
  buildTaskClipboardText(): string {
    const parts: string[] = [this.taskData.nameTask.trim()];

    // The description is rich text, so it has to be flattened: pasting the raw
    // field dropped the markup (<p>, <br>, <li>…) straight into the clipboard.
    const description = this.htmlToPlainText(this.taskData.content);
    parts.push(description || '(no description)');

    if (this.checklist.length) {
      const lines = this.checklist.map(i => `- [${i.done ? 'x' : ' '}] ${i.label}`);
      parts.push(`Checklist (${this.checklistDoneCount}/${this.checklist.length})\n${lines.join('\n')}`);
    }

    return parts.join('\n\n');
  }

  /**
   * Rich-text HTML as readable plain text: block ends become newlines, list items
   * become dashes, entities are decoded, and runs of blank lines are collapsed.
   */
  private htmlToPlainText(html: string | null | undefined): string {
    if (!html) return '';
    if (!/<[a-z][\s\S]*>/i.test(html)) return html.trim();

    const withBreaks = html
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\s*li[^>]*>/gi, '\n- ')
      .replace(/<\s*\/\s*(p|div|li|h[1-6]|tr|ul|ol|blockquote)\s*>/gi, '\n');

    // Decoding through the DOM also strips the remaining tags and handles entities.
    const el = document.createElement('div');
    el.innerHTML = withBreaks;
    return (el.textContent || '')
      .replace(/\u00a0/g, ' ')
      .split('\n')
      .map(line => line.trimEnd())
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  async copyTaskContent() {
    const ok = await this.clipboardService.copy(this.buildTaskClipboardText());
    if (!ok) return;

    this.copied = true;
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => (this.copied = false), 2000);
  }

  // ── Checklist ───────────────────────────────────────────────────────────────

  checklist: ChecklistItem[] = [];
  loadingChecklist = false;
  /** Surfaced in the card: a swallowed failure here reads as "the button does nothing". */
  checklistError: string | null = null;

  newChecklistLabel = '';
  showChecklistAdd = false;

  checklistTemplates: ChecklistTemplate[] = [];
  showTemplateMenu = false;
  savingTemplate = false;
  /** Name typed in the "save as template" field, empty when that field is hidden. */
  newTemplateName: string | null = null;

  get checklistDoneCount(): number {
    return this.checklist.filter(i => i.done).length;
  }

  get checklistPercent(): number {
    if (!this.checklist.length) return 0;
    return Math.round((this.checklistDoneCount / this.checklist.length) * 100);
  }

  /**
   * A task being created has no id yet, so its checklist is held in memory and
   * written once the task exists. Draft items carry a local id so the template's
   * `track` and the remove button still work before the round trip.
   */
  private draftIdCounter = 0;

  /** True while composing a checklist for a task that has not been created yet. */
  get isDraftChecklist(): boolean {
    return !this.taskToEdit?.id;
  }

  async loadChecklist() {
    const taskId = this.taskToEdit?.id;
    if (!taskId) return;
    this.loadingChecklist = true;
    try {
      this.checklist = await this.checklistService.listItems(taskId);
    } finally {
      this.loadingChecklist = false;
    }
  }

  /** Persist the drafted checklist against the task that was just created. */
  private async flushDraftChecklist(taskId: string) {
    if (!this.checklist.length) return;
    for (const [index, item] of this.checklist.entries()) {
      await this.checklistService.addItem(taskId, item.label, index);
    }
  }

  toggleChecklistAdd() {
    this.showChecklistAdd = !this.showChecklistAdd;
    this.newChecklistLabel = '';
  }

  async addChecklistItem() {
    const label = this.newChecklistLabel.trim();
    if (!label) return;
    this.newChecklistLabel = '';

    const taskId = this.taskToEdit?.id;
    if (!taskId) {
      this.checklist = [...this.checklist, this.draftItem(label)];
      return;
    }

    const created = await this.checklistService.addItem(taskId, label, this.checklist.length);
    if (created) this.checklist = [...this.checklist, created];
  }

  private draftItem(label: string): ChecklistItem {
    return {
      id: `draft-${this.draftIdCounter++}`,
      taskId: '',
      label,
      done: false,
      order: this.checklist.length,
      doneBy: null,
      doneAt: null,
    };
  }

  /** Optimistic tick: the box responds immediately and reverts if the write fails. */
  async toggleChecklistItem(item: ChecklistItem) {
    if (!item.id || this.isTaskDone) return;
    const next = !item.done;
    item.done = next;
    if (this.isDraftChecklist) return; // nothing to write yet
    const ok = await this.checklistService.setDone(item.id, next);
    if (!ok) item.done = !next;
  }

  async removeChecklistItem(item: ChecklistItem) {
    if (!item.id) return;
    const previous = this.checklist;
    this.checklist = this.checklist.filter(i => i.id !== item.id);
    if (this.isDraftChecklist) return;
    const ok = await this.checklistService.deleteItem(item.id);
    if (!ok) this.checklist = previous;
  }

  async openTemplateMenu() {
    this.showTemplateMenu = !this.showTemplateMenu;
    this.newTemplateName = null;
    if (this.showTemplateMenu) {
      this.checklistTemplates = await this.checklistService.listTemplates();
    }
  }

  async applyTemplate(template: ChecklistTemplate) {
    this.showTemplateMenu = false;
    const taskId = this.taskToEdit?.id;
    if (!taskId) {
      // Drafting: stack the template's lines onto the in-memory list.
      this.checklist = [...this.checklist, ...template.items.map(label => this.draftItem(label))];
      return;
    }
    try {
      await this.checklistService.applyTemplate(taskId, template, this.checklist.length);
      await this.loadChecklist();
    } catch (e) {
      console.error('[AddTask] Failed to apply checklist template', e);
    }
  }

  startSavingTemplate() {
    this.newTemplateName = '';
  }

  /** Turn the task's current checklist into a reusable template. */
  async saveChecklistAsTemplate() {
    const name = (this.newTemplateName ?? '').trim();
    if (!name || !this.checklist.length || this.savingTemplate) return;
    this.savingTemplate = true;
    try {
      await this.checklistService.createTemplate(
        name,
        this.checklist.map(i => i.label),
        this.projectTenantId ?? null
      );
      this.newTemplateName = null;
      this.showTemplateMenu = false;
    } catch (e) {
      console.error('[AddTask] Failed to save checklist template', e);
    } finally {
      this.savingTemplate = false;
    }
  }

  async deleteTemplate(template: ChecklistTemplate, event: Event) {
    event.stopPropagation();
    if (!template.id) return;
    this.checklistTemplates = this.checklistTemplates.filter(t => t.id !== template.id);
    await this.checklistService.deleteTemplate(template.id);
  }

  // ── Right panel: conversation + change history ──────────────────────────────

  /** Which stream the right rail is showing. */
  sidePanel: 'conversation' | 'history' = 'conversation';

  auditEntries: TaskAuditEntry[] = [];
  loadingAudit = false;

  async loadAuditTrail() {
    const taskId = this.taskToEdit?.id;
    if (!taskId) return;
    this.loadingAudit = true;
    try {
      // Newest first reads better in a feed, even though the API returns ascending.
      this.auditEntries = (await this.taskService.listAudits(taskId)).reverse();
    } finally {
      this.loadingAudit = false;
    }
  }

  /** Human label for an audited field, e.g. `limitDateToDo` -> "Due date". */
  auditFieldLabel(field: string): string {
    const labels: Record<string, string> = {
      nameTask: 'Name',
      content: 'Description',
      status: 'Status',
      priority: 'Priority',
      startDate: 'Start date',
      limitDateToDo: 'Due date',
      estimatedHours: 'Estimated hours',
      employeeId: 'Assignees',
      projectStageId: 'Phase',
      links: 'Links',
      task: 'Task',
    };
    return labels[field] ?? field;
  }

  /**
   * Render a stored value for display. Assignee ids become names; empty stays
   * an em dash so a cleared field reads as such.
   */
  auditValueLabel(field: string, value: string | null | undefined): string {
    if (!value) return '—';
    if (field === 'employeeId') {
      return value
        .split(',')
        .map(id => {
          const emp = this.employees.find(e => e.id === id);
          return emp ? `${emp.firstName ?? ''} ${emp.name ?? ''}`.trim() : 'Unknown';
        })
        .join(', ');
    }
    if (field === 'status') {
      return { NOTBEGIN: 'New', INPROGRESS: 'In Progress', DONE: 'Done' }[value] ?? value;
    }
    if (field === 'priority') {
      return { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' }[value] ?? value;
    }
    if (field === 'projectStageId') {
      // Stored as a raw id; show the phase name so the feed stays readable.
      return this.stageNames.get(value) ?? 'Unknown phase';
    }
    return value;
  }

  /** Phase id → name, for rendering `projectStageId` changes in the history feed. */
  private stageNames = new Map<string, string>();
  private allStages: ProjectStage[] = [];

  private loadStageNames() {
    this.projectService.projectsStage$.subscribe(stages => {
      this.allStages = stages;
      this.stageNames = new Map(
        stages
          .filter(st => !!st.projectStageId)
          .map(st => [st.projectStageId!, st.stageName])
      );
    });
  }

  /**
   * Phases of the task's own project, for the "move to another phase" picker.
   * Filtered at render time rather than in the subscription because
   * `currentProjectId` is resolved asynchronously, after the first emission.
   */
  get projectPhases(): ProjectStage[] {
    if (!this.currentProjectId) return [];
    return this.allStages
      .filter(st => st.projectId === this.currentProjectId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  /** True once the task has been pointed at a different phase than the saved one. */
  get isMovingPhase(): boolean {
    return !!this.taskToEdit
      && !!this.taskData.projectStageId
      && this.taskData.projectStageId !== this.taskToEdit.projectStageId;
  }

  auditAuthorName(userId: string): string {
    const emp = this.employees.find(e => e.id === userId);
    return emp ? `${emp.firstName ?? ''} ${emp.name ?? ''}`.trim() || 'Unknown' : 'Unknown';
  }

  auditAuthorInitials(userId: string): string {
    const emp = this.employees.find(e => e.id === userId);
    if (!emp) return '?';
    return `${emp.firstName?.charAt(0) || '?'}${emp.name?.charAt(0) || ''}`.toUpperCase();
  }

  canUserAccessTask(): boolean {
    if (this.user.admin()) return true;
    return true; // Open system for now
  }

  // ---- Read-only view helpers ----
  getStatusInfo(): { label: string; classes: string } {
    switch (this.taskData.status) {
      case 'INPROGRESS': return { label: 'In Progress', classes: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'DONE': return { label: 'Done', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      default: return { label: 'New', classes: 'bg-slate-50 text-slate-600 border-slate-200' };
    }
  }

  getPriorityInfo(): { label: string; classes: string; dot: string } {
    switch (this.taskData.priority) {
      case 'HIGH': return { label: 'High', classes: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' };
      case 'MEDIUM': return { label: 'Medium', classes: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' };
      default: return { label: 'Low', classes: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
    }
  }

  /** Employees assigned to this task (for the read-only view / selected chips). */
  getAssignedEmployees(): UserModel[] {
    return this.employees.filter(e => e.id && this.taskData.employeeId.includes(e.id));
  }

  /** True when the due date has passed and the task isn't done (read-only view). */
  get isOverdue(): boolean {
    if (!this.dueDateValue || this.taskData.status === 'DONE') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(this.dueDateValue);
    due.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
  }

  /** Human-readable distance to the due date, e.g. "Today", "In 3 days", "2 days overdue". */
  get dueRelative(): string {
    if (!this.dueDateValue) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(this.dueDateValue);
    due.setHours(0, 0, 0, 0);
    const diff = Math.round((due.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    if (diff < 0) return `${-diff} days overdue`;
    return `In ${diff} days`;
  }

  /** The employee who validated the task, if any (read-only view). */
  get validatedByEmployee(): UserModel | null {
    const id = this.taskToEdit?.validatedBy;
    if (!id) return null;
    return this.employees.find(e => e.id === id) || null;
  }

  /** Solid accent color for the details-view hero strip, keyed to status. */
  get statusAccent(): string {
    switch (this.taskData.status) {
      case 'INPROGRESS': return 'bg-blue-500';
      case 'DONE': return 'bg-emerald-500';
      default: return 'bg-slate-400';
    }
  }

  /** Search term for filtering the assignee list. */
  assigneeSearch = '';

  /** Employees matching the assignee search box. */
  getFilteredEmployees(): UserModel[] {
    const q = this.assigneeSearch.trim().toLowerCase();
    const selectable = this.selectableEmployees;
    if (!q) return selectable;
    return selectable.filter(e =>
      `${e.firstName ?? ''} ${e.name ?? ''}`.toLowerCase().includes(q) ||
      (e.email ?? '').toLowerCase().includes(q)
    );
  }

  getInitials(emp: UserModel): string {
    return `${emp.firstName?.charAt(0) || '?'}${emp.name?.charAt(0) || ''}`.toUpperCase();
  }

  onStartDateChange(value: Date | null) {
    if (value) {
      this.startDateValue = value;
      this.taskData.startDate = this.toIsoDay(value);
      if (this.dueDateValue && this.dueDateValue < value) {
        this.dueDateValue = new Date(value);
        this.dueDateValue.setDate(this.dueDateValue.getDate() + 7);
        this.taskData.limitDateToDo = this.toIsoDay(this.dueDateValue);
      }
    } else {
      this.startDateValue = null;
      this.taskData.startDate = '';
    }
  }

  onDueDateChange(value: Date | null) {
    if (value) {
      this.dueDateValue = value;
      this.taskData.limitDateToDo = this.toIsoDay(value);
    } else {
      this.dueDateValue = null;
      this.taskData.limitDateToDo = '';
    }
  }

  /**
   * Employees offered in the assignee picker: active ones only, plus any
   * archived employee already assigned to the task so we don't silently
   * drop them when the task is edited.
   */
  get selectableEmployees(): UserModel[] {
    return this.employees.filter(
      emp => !emp.isArchived || (emp.id ? this.taskData.employeeId.includes(emp.id) : false)
    );
  }

  isEmployeeSelected(employeeId: string | undefined): boolean {
    if (!employeeId) return false;
    return this.taskData.employeeId.includes(employeeId);
  }

  toggleEmployeeSelection(employeeId: string | undefined) {
    if (!employeeId) return;
    const index = this.taskData.employeeId.indexOf(employeeId);
    if (index > -1) {
      this.taskData.employeeId = this.taskData.employeeId.filter(id => id !== employeeId);
    } else {
      this.taskData.employeeId = [...this.taskData.employeeId, employeeId];
    }
  }

  // ---- Task links (Drive & co) ----

  showAddLinkForm = false;
  newLinkUrl = '';
  newLinkName = 'Drive';
  newLinkType: 'Drive' | 'Other' = 'Drive';

  toggleAddLink() {
    this.showAddLinkForm = !this.showAddLinkForm;
    this.resetLinkForm();
  }

  onLinkTypeChange() {
    this.newLinkName = this.newLinkType === 'Drive' ? 'Drive' : '';
  }

  addLink() {
    const url = this.newLinkUrl.trim();
    const name = this.newLinkName.trim();
    if (!url || !name) return;

    try {
      new URL(url);
    } catch {
      alert('Please enter a valid URL (e.g. https://drive.google.com/...)');
      return;
    }

    this.taskData.links = [...this.taskData.links, { name, url }];
    this.showAddLinkForm = false;
    this.resetLinkForm();
  }

  removeLink(index: number) {
    this.taskData.links = this.taskData.links.filter((_, i) => i !== index);
  }

  cancelAddLink() {
    this.showAddLinkForm = false;
    this.resetLinkForm();
  }

  private resetLinkForm() {
    this.newLinkUrl = '';
    this.newLinkName = 'Drive';
    this.newLinkType = 'Drive';
  }

  /** True when the link points at Google Drive, so the view can show the Drive icon. */
  isDriveLink(link: ProjectLink): boolean {
    return /drive\.google\.com|docs\.google\.com/.test(link?.url || '');
  }

  openExternalLink(url: string, event?: Event): void {
    if (url) this.externalLinkService.openExternal(url, event);
  }

  /**
   * Cancel from the edit form. Editing an existing task returns to its read-only
   * view; creating one has nothing to fall back to, so it closes — which the page
   * host turns into a navigation.
   */
  onCancel() {
    if (this.taskToEdit) this.mode = 'view';
    else this.close.emit();
  }

  async onSubmit() {
    if (this.isDueDateMissing) return;
    this.isSubmitting = true;
    try {
      let parsedEstimatedTime = 0;
      if (this.taskData.estimatedTime) {
        const timeStr = String(this.taskData.estimatedTime).replace(',', '.');
        parsedEstimatedTime = parseFloat(timeStr);
        if (isNaN(parsedEstimatedTime)) {
          parsedEstimatedTime = 0;
        }
      }

      // Capture previous assignments to detect newly added users
      const previousEmployeeIds = this.taskToEdit?.employeeId?.filter((id): id is string => id !== null) ?? [];

      const updates: Partial<Task> = {
        nameTask: this.taskData.nameTask,
        content: this.taskData.content,
        status: this.taskData.status,
        priority: this.taskData.priority,
        estimatedHours: parsedEstimatedTime,
        startDate: this.taskData.startDate ? new Date(this.taskData.startDate + 'T12:00:00').toISOString() : null,
        limitDateToDo: this.taskData.limitDateToDo ? new Date(this.taskData.limitDateToDo + 'T12:00:00').toISOString() : null,
        employeeId: this.taskData.employeeId,
        projectStageId: this.taskData.projectStageId || this.projectStageId,
        links: this.taskData.links,
      };

      let savedTaskId: string | undefined;
      if (this.taskToEdit?.id) {
        await this.taskService.updateTask(this.taskToEdit.id, updates);
        savedTaskId = this.taskToEdit.id;
      } else {
        const created = await this.taskService.createTask(updates as Task);
        savedTaskId = created?.id;
        // The checklist was drafted before the task existed; write it now.
        if (savedTaskId) await this.flushDraftChecklist(savedTaskId);
      }

      // Notify newly assigned users (fire-and-forget, don't block the UI)
      const currentUser = this.userService.getCurrentUserSnapshot();
      if (currentUser?.id && savedTaskId) {
        const senderName = [currentUser.firstName, currentUser.name].filter(Boolean).join(' ') || 'A colleague';
        this.notificationService.notifyTaskAssignment({
          taskName: this.taskData.nameTask,
          newEmployeeIds: this.taskData.employeeId,
          previousEmployeeIds,
          currentUserId: currentUser.id,
          senderName,
          taskId: savedTaskId,
          projectId: this.currentProjectId || undefined,
        }).catch(e => console.error('[AddTask] Notification error:', e));
      }

      if (this.taskToEdit?.id) {
        // Saving an edit lands back on the task itself, not out on the project's
        // phase list. The service holds the fresh row; patch our copy so the
        // read-only view shows what was just saved.
        await this.loadAuditTrail();
        // Mutated in place, not replaced: the host page renders its breadcrumb
        // from the same object, so a fresh copy here would leave the old task
        // name on screen after a rename.
        Object.assign(this.taskToEdit, updates);
        this.populateEditData();
        this.mode = 'view';
        return;
      }

      this.close.emit();
    } catch (err) {
      console.error("Error saving task:", err);
      alert("Error while saving task.");
    } finally {
      this.isSubmitting = false;
    }
  }

  deleteTask() {
    if (!this.taskToEdit?.id) return;
    this.confirmMessage = 'Are you sure you want to delete this task?';
    this.showConfirm = true;
  }

  handleConfirm(result: 'ok' | 'cancel') {
    this.showConfirm = false;
    if (result === 'ok' && this.taskToEdit?.id) {
      this.taskService.deleteTask(this.taskToEdit.id)
        .then(() => {
          this.taskDeleted.emit();
          this.close.emit();
        })
        .catch(err => console.error(err));
    }
  }

}
